/**
 * Single chokepoint for GA4-bound dataLayer pushes (SPEC-027 FR-3).
 *
 * Every GA4 event goes through here so the `client_id` / `session_id` /
 * `user_id` triple is merged uniformly. Null identifiers (anonymous users
 * with capture blocked) are omitted per decision D1.
 *
 * The same event is mirrored to PostHog so both analytics backends stay in
 * sync from one call site. PostHog associates events with the person via the
 * identify()/reset() lifecycle (see utils/posthog), so we forward the GA
 * taxonomy as event properties rather than re-sending identifiers here.
 */
import { getGa4Identifiers } from './ga4Identifiers'
import { capturePosthogEvent } from './posthog'

// Derive a human-readable PostHog event name from the GA payload. Most events
// come through as ('GTMEvent', { eventCategory, eventAction, ... }), where the
// dataLayer `event` is a constant wrapper and the real meaning lives in the
// category/action pair. Ecommerce-style calls (e.g. 'purchase') carry no
// category, so we fall back to the dataLayer event name itself.
const posthogEventName = (event, { eventCategory, eventAction } = {}) => {
  if (eventCategory && eventAction) return `${eventCategory} — ${eventAction}`
  return eventCategory || event
}

const pushGa4Event = (event, params = {}, userId) => {
  const ids = getGa4Identifiers(userId)
  const cleanedIds = Object.fromEntries(
    Object.entries(ids).filter(([, value]) => value != null)
  )
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event,
    ...params,
    ...cleanedIds // client_id (string), session_id (number), user_id (string)
  })

  // Mirror to PostHog (no-ops until PostHog is initialized).
  capturePosthogEvent(posthogEventName(event, params), params)
}

export default pushGa4Event
